import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../../api';
import withUrlParam from '../../hocs/withUrlParam';
import Loader from '../../components/Loader';
import CopyButton from '../../components/CopyButton';
import { useHttpGetGatewayMetadata } from '../../hooks';
import GatewayLivePanel from './GatewayLivePanel';
import GatewayHealthHistory from './GatewayHealthHistory';
import AddressMapPopup from './AddressMapPopup';
import EditBatteryManufacturerModal from './EditBatteryManufacturerModal';
import EditBatteryCapacityModal from './EditBatteryCapacityModal';
import DeleteGatewayModal from './DeleteGatewayModal';

const GENERIC_BATTERY_ICON = '/assets/icons/battery.svg';

// Best-effort brand logo for a free-text battery manufacturer. Falls back to the generic
// battery icon (via the <img> onError below) when no matching logo asset exists, so dropping
// a `<brand>.png` into public/assets/icons/logos/ later automatically starts showing it.
const batteryLogoSrc = brand => {
    const key = (brand || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return key ? `/assets/icons/logos/${key}.png` : GENERIC_BATTERY_ICON;
};

const STATUS_META = {
    CONNECTED: { label: 'Connected', className: 'gw--connected' },
    LOST: { label: 'Lost', className: 'gw--lost' },
};

const statusMetaFor = status => STATUS_META[status] || { label: 'Unknown', className: 'gw--unknown' };

const formatDateTime = value => {
    if (!value) {
        return '—';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

// Compact relative time ("3 h ago"), shown alongside the absolute timestamp.
const timeAgo = value => {
    if (!value) {
        return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '—';
    }
    const mins = Math.round((Date.now() - date.getTime()) / 60000);
    if (mins < 1) {
        return 'just now';
    }
    if (mins < 60) {
        return `${mins} min ago`;
    }
    const hours = Math.round(mins / 60);
    if (hours < 24) {
        return `${hours} h ago`;
    }
    return `${Math.round(hours / 24)} d ago`;
};

const clean = value => {
    if (value === null || value === undefined || value === '' || value === 'unknown') {
        return null;
    }
    return value;
};

const Field = ({ label, value, mono, copyable, to, href, node }) => {
    const v = clean(value);
    let content = '—';
    if (node && (v || value)) {
        content = node;
    } else if (v && to) {
        content = (
            <Link className="gw-link" to={to}>
                {v}
            </Link>
        );
    } else if (v && href) {
        content = (
            <a className="gw-link" href={href} target="_blank" rel="noreferrer">
                {v}
            </a>
        );
    } else if (v) {
        content = v;
    }
    return (
        <div className="gw-field">
            <span className="gw-field__label">{label}</span>
            <span className={`gw-field__value${mono ? ' gw-field__value--mono' : ''}`}>
                {content}
                {v && copyable ? <CopyButton value={v} /> : null}
            </span>
        </div>
    );
};

const GatewayPage = ({ paramValue: deviceId }) => {
    const location = useLocation();
    // The overview passes the gateway row through router state so we can show name/serial/ids
    // immediately. On a direct load/refresh there is no state, so we fall back to the device id.
    const gw = location.state?.gateway ?? null;

    const [health, setHealth] = useState(null);
    const [pageTab, setPageTab] = useState('overview');
    const [followsSchedule, setFollowsSchedule] = useState(null);
    const [isReady, setIsReady] = useState(false);
    // Battery manufacturer is user-supplied (stored on the gateway, not device-reported).
    // Kept in local state so an inline edit reflects immediately without a metadata refetch.
    const [batteryManufacturer, setBatteryManufacturer] = useState(null);
    const [editBatteryOpen, setEditBatteryOpen] = useState(false);
    // Resolved capacity: the effective figure, which source produced it, and whether the
    // inverter disagrees. Kept in local state so an inline edit shows immediately.
    const [batteryCapacity, setBatteryCapacity] = useState(null);
    const [editCapacityOpen, setEditCapacityOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const { metadata, isReady: metadataReady } = useHttpGetGatewayMetadata(deviceId);

    useEffect(() => {
        setBatteryManufacturer(metadata?.battery_manufacturer ?? null);
        setBatteryCapacity(metadata?.battery_capacity ?? null);
    }, [metadata]);

    useEffect(() => {
        let active = true;
        setIsReady(false);

        const load = async () => {
            const [healthRes, follows] = await Promise.all([
                api.get_device_gateway_health(deviceId).catch(() => null),
                api.get_device_follows_schedule(deviceId).catch(() => null),
            ]);
            if (!active) {
                return;
            }
            setHealth(healthRes?.data ?? null);
            setFollowsSchedule(follows ?? null);
            setIsReady(true);
        };

        load();
        return () => {
            active = false;
        };
    }, [deviceId]);

    if (!isReady) {
        return <Loader />;
    }

    const status = (health?.status || '').toUpperCase();
    const statusMeta = statusMetaFor(status);
    const name = clean(gw?.name) ?? clean(gw?.custom_device_name) ?? 'Gateway';
    const customerId = clean(gw?.retailer_customer_id);
    const address = clean(gw?.address);
    const lastSeen = health?.last_heartbeat;

    return (
        <div className="gateway-page">
            <Link className="gateway-page__back" to="/gateways">
                <i className="fa-solid fa-arrow-left" /> <span>All gateways</span>
            </Link>

            {/* Hero header */}
            <div className="gateway-page__hero">
                <div className="gateway-page__hero-main">
                    <div className="gateway-page__hero-icon">
                        {/* Cloud-link gateway icon, inlined so it inherits the hero color. */}
                        <svg width="30" height="30" viewBox="3.5 4 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M8 9a3 3 0 01.4-1.5A3.2 3.2 0 0114.6 8 2.6 2.6 0 0117 13H8.5A2.75 2.75 0 018 9z"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinejoin="round"
                            />
                            <path d="M12 13v3" stroke="currentColor" strokeWidth="1.5" />
                            <rect x="6.5" y="16" width="11" height="4.5" rx="1.4" stroke="currentColor" strokeWidth="1.5" />
                            <circle cx="9" cy="18.2" r="0.85" fill="currentColor" />
                        </svg>
                    </div>
                    <div>
                        <h1 className="gateway-page__name">{name}</h1>
                        <div className="gateway-page__sub">
                            {clean(gw?.reference) ? <span>Serial {gw.reference}</span> : null}
                            {customerId ? (
                                <span>
                                    <Link className="gw-link" to={`/devicesdetail/${customerId}`}>
                                        {customerId}
                                    </Link>
                                </span>
                            ) : null}
                        </div>
                    </div>
                </div>
                <span className={`gw-pill ${statusMeta.className}`}>
                    <span className="gw-pill__dot" />
                    {statusMeta.label}
                </span>
            </div>

            <div className="gateway-page__tabs">
                <button
                    type="button"
                    className={`gateway-page__tab ${pageTab === 'overview' ? 'gateway-page__tab--active' : ''}`}
                    onClick={() => setPageTab('overview')}
                >
                    Overview
                </button>
                <button
                    type="button"
                    className={`gateway-page__tab ${pageTab === 'health' ? 'gateway-page__tab--active' : ''}`}
                    onClick={() => setPageTab('health')}
                >
                    Health
                </button>
            </div>

            {pageTab === 'health' ? (
                <GatewayHealthHistory deviceId={deviceId} />
            ) : (
                <>
                    {/* Device + inverter details */}
                    <div className="gateway-page__columns">
                        <div className="gw-card">
                            <div className="gw-card__header">
                                <span className="gw-card__header-title">
                                    <i className="fa-solid fa-circle-info" /> Device
                                </span>
                                {lastSeen ? (
                                    <span className="gw-card__header-meta">
                                        Last seen {timeAgo(lastSeen)} · {formatDateTime(lastSeen)}
                                    </span>
                                ) : null}
                            </div>
                            <div className="gw-card__grid">
                                <Field label="Name" value={gw?.name} />
                                <Field label="Serial number" value={gw?.reference} copyable />
                                <Field label="Customer" value={customerId} to={customerId ? `/devicesdetail/${customerId}` : undefined} />
                                <Field label="Address" value={address} node={address ? <AddressMapPopup address={address} /> : undefined} />
                                <Field label="Device ID" value={deviceId} mono copyable />
                                <Field label="Gateway firmware" value={clean(health?.version) ?? clean(metadata?.gateway_firmware_version)} />
                            </div>
                        </div>

                        <div className="gw-card">
                            <div className="gw-card__header">
                                <span className="gw-card__header-title">
                                    <i className="fa-solid fa-microchip" /> Inverter &amp; hardware
                                </span>
                            </div>
                            {!metadataReady ? (
                                <Loader />
                            ) : metadata ? (
                                <div className="gw-card__grid">
                                    <Field label="Inverter manufacturer" value={metadata.inverter_manufacturer} />
                                    <Field label="Inverter model" value={metadata.inverter_model} />
                                    <Field label="Inverter serial" value={metadata.inverter_serial_number} mono copyable />
                                    <Field label="Inverter firmware" value={metadata.inverter_firmware_version} />
                                    <Field label="Gateway hardware" value={metadata.gateway_hardware_version} />
                                    <Field label="Gateway firmware" value={metadata.gateway_firmware_version} />
                                    <div className="gw-field">
                                        <span className="gw-field__label">Battery manufacturer</span>
                                        <span className="gw-field__value">
                                            <img
                                                src={batteryLogoSrc(batteryManufacturer)}
                                                onError={e => {
                                                    e.target.onerror = null;
                                                    e.target.src = GENERIC_BATTERY_ICON;
                                                }}
                                                alt=""
                                                style={{ height: 18, marginRight: 8, verticalAlign: 'middle' }}
                                            />
                                            {batteryManufacturer || 'Not set'}
                                            <button
                                                type="button"
                                                className="gw-link"
                                                onClick={() => setEditBatteryOpen(true)}
                                                style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                                title="Edit battery manufacturer"
                                            >
                                                <i className="fa-solid fa-pen" />
                                            </button>
                                        </span>
                                    </div>
                                    <div className="gw-field">
                                        <span className="gw-field__label">Battery capacity</span>
                                        <span className="gw-field__value">
                                            {batteryCapacity?.capacity_kwh != null ? `${batteryCapacity.capacity_kwh} kWh` : 'Not set'}
                                            {batteryCapacity?.source === 'override' && (
                                                <span style={{ marginLeft: 6, fontSize: 12, color: '#6b7280' }}>(manually set)</span>
                                            )}
                                            {batteryCapacity?.diverges && (
                                                <span
                                                    style={{ marginLeft: 6, fontSize: 12, color: '#b45309' }}
                                                    title={`The inverter reports ${batteryCapacity.device_reported_kwh} kWh`}
                                                >
                                                    differs from inverter
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                className="gw-link"
                                                onClick={() => setEditCapacityOpen(true)}
                                                style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                                title="Edit battery capacity"
                                                aria-label="Edit battery capacity"
                                            >
                                                <i className="fa-solid fa-pen" aria-hidden="true" />
                                            </button>
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <p className="gw-empty">No hardware metadata reported by this gateway yet.</p>
                            )}
                        </div>
                    </div>

                    <EditBatteryManufacturerModal
                        deviceId={deviceId}
                        current={batteryManufacturer}
                        open={editBatteryOpen}
                        setOpen={setEditBatteryOpen}
                        onSaved={setBatteryManufacturer}
                    />

                    <EditBatteryCapacityModal
                        deviceId={deviceId}
                        capacity={batteryCapacity}
                        open={editCapacityOpen}
                        setOpen={setEditCapacityOpen}
                        onSaved={setBatteryCapacity}
                    />

                    {/* Data card: always-on live readings, then a Live / Historical tab below.
                        Self-contained so its per-sample SSE updates re-render only itself. */}
                    <GatewayLivePanel deviceId={deviceId} />

                    {/* Schedule */}
                    <div className="gw-card">
                        <div className="gw-card__header">
                            <span className="gw-card__header-title">
                                <i className="fa-regular fa-calendar" /> Schedule
                            </span>
                        </div>
                        <div className="gw-card__row">
                            <div className="gw-field">
                                <span className="gw-field__label">Follows mode schedule</span>
                                <span className="gw-field__value">
                                    {followsSchedule === null ? (
                                        '—'
                                    ) : (
                                        <span className={`gw-badge ${followsSchedule ? 'gw-badge--on' : 'gw-badge--off'}`}>
                                            {followsSchedule ? 'Yes' : 'No'}
                                        </span>
                                    )}
                                </span>
                            </div>
                            <Link className="gw-link-btn" to={`/devices/${deviceId}/mode-scheduler`}>
                                Open scheduler <i className="fa-solid fa-arrow-right" />
                            </Link>
                        </div>
                    </div>

                    {/* Danger zone — visually apart from the everyday actions above, so deletion
                        is never one stray click away from editing a battery value. */}
                    <div className="gw-card" style={{ borderColor: '#fca5a5' }}>
                        <div className="gw-card__header">
                            <span className="gw-card__header-title" style={{ color: '#b91c1c' }}>
                                <i className="fa-solid fa-triangle-exclamation" /> Danger zone
                            </span>
                        </div>
                        <div className="gw-card__row">
                            <div className="gw-field">
                                <span className="gw-field__label">Delete this gateway</span>
                                <span className="gw-field__value" style={{ fontSize: 13, color: '#6b7280' }}>
                                    Stops remote control and removes it from the fleet. Re-onboarding is the only path back.
                                </span>
                            </div>
                            <button type="button" className="button" onClick={() => setDeleteOpen(true)} style={{ backgroundColor: '#b91c1c' }}>
                                Delete gateway
                            </button>
                        </div>
                    </div>

                    <DeleteGatewayModal
                        deviceId={deviceId}
                        gatewayName={clean(gw?.name)}
                        serial={clean(gw?.reference)}
                        open={deleteOpen}
                        setOpen={setDeleteOpen}
                    />
                </>
            )}
        </div>
    );
};

export default withUrlParam('deviceId')(GatewayPage);
